import React, { useState, useEffect } from 'react';
import Modal from 'react-modal';
import Button from '../../Button';
import useToast from '../../../hooks/useToast';
import { documentsService, fetchGlobalSearch, Document } from '../../../services/documents.service';
import { api } from '../../../services/api';
import { TitleHeader, FormContainer, TabContainer, Tab, SearchResult } from './styles';

interface ModalProps {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  locationId: string;
}

const customStyles = {
  content: {
    top: '50%',
    left: '50%',
    right: 'auto',
    bottom: 'auto',
    marginRight: '-50%',
    transform: 'translate(-50%, -50%)',
    width: '600px',
    maxHeight: '80vh',
    borderRadius: '8px',
    padding: '24px',
    overflowY: 'auto' as 'auto',
  },
  overlay: {
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    zIndex: 1000,
  },
};

const ModalLinkDocument: React.FC<ModalProps> = ({ isOpen, setIsOpen, locationId }) => {
  const { successToast, errorToast } = useToast();
  const [tab, setTab] = useState<'search' | 'create'>('search');
  
  // Search state
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState<Document[]>([]);
  const [searching, setSearching] = useState(false);

  // Create state
  const [docName, setDocName] = useState('');
  const [docType, setDocType] = useState('');
  const [creating, setCreating] = useState(false);
  const [documentTypes, setDocumentTypes] = useState<any[]>([]);

  useEffect(() => {
    if (isOpen) {
      setTab('search');
      setSearchTerm('');
      setSearchResults([]);
      setDocName('');
      setDocType('');

      // Fetch document types
      api.get('/documentTypes?perPage=1000')
        .then(res => setDocumentTypes(res.data?.data || []))
        .catch(err => console.error(err));
    }
  }, [isOpen]);

  // Debounced search
  useEffect(() => {
    if (tab !== 'search' || searchTerm.length < 3) {
      setSearchResults([]);
      return;
    }
    
    const delayDebounceFn = setTimeout(() => {
      setSearching(true);
      fetchGlobalSearch(1, 10, searchTerm)
        .then(res => {
          setSearchResults(res.data.filter(d => !d.isFolder));
        })
        .catch(err => {
          console.error(err);
        })
        .finally(() => {
          setSearching(false);
        });
    }, 500);

    return () => clearTimeout(delayDebounceFn);
  }, [searchTerm, tab]);

  const handleLink = async (doc: Document) => {
    try {
      await documentsService.updateDocument(doc._id, { physicalLocation: locationId });
      
      // Se o documento não tiver um tipo definido, e o usuário selecionou um tipo válido (diferente de sem_tipo)
      if (docType !== 'sem_tipo' && (!doc.documentTypeWithData || !(doc.documentTypeWithData as any).documentType)) {
        await api.put('/documents/documentType', { document: doc._id, documentType: docType, metadata: [] });
      }

      successToast('Documento vinculado com sucesso!');
      setIsOpen(false);
    } catch (err: any) {
      errorToast(err?.response?.data?.message || 'Erro ao vincular documento');
    }
  };

  const handleCreate = async (e: any) => {
    e.preventDefault();
    if (!docName.trim()) return;
    if (!docType) {
      errorToast('Selecione um Tipo de Documento');
      return;
    }

    try {
      setCreating(true);
      const data: any = {
        name: docName,
        physicalLocation: locationId,
      };
      // Se não for "sem_tipo", passamos o tipo
      if (docType !== 'sem_tipo') {
        data.documentType = docType;
      }

      await documentsService.createPhysicalDocument(data);
      successToast('Documento físico criado e vinculado com sucesso!');
      setIsOpen(false);
    } catch (err: any) {
      errorToast(err?.response?.data?.message || 'Erro ao criar documento');
    } finally {
      setCreating(false);
    }
  };

  const buildPath = (doc: any) => {
    if (doc.ancestors && Array.isArray(doc.ancestors) && doc.ancestors.length > 0) {
      const sorted = [...doc.ancestors].sort((a, b) => b.depth - a.depth);
      return '/' + sorted.map(a => a.name).join('/');
    }
    return doc.path || '/';
  };

  return (
    <Modal isOpen={isOpen} onRequestClose={() => setIsOpen(false)} style={customStyles} ariaHideApp={false}>
      <TitleHeader>
        <h3 style={{ color: '#333' }}>Vincular Documento</h3>
        <button onClick={() => setIsOpen(false)} style={{ background: 'transparent', border: 'none', cursor: 'pointer', fontSize: '18px', color: '#333' }}>X</button>
      </TitleHeader>

      <div style={{ marginBottom: '20px' }}>
        <label style={{ fontWeight: 'bold', display: 'block', marginBottom: '5px', color: '#333' }}>Tipo de Documento</label>
        <select value={docType} onChange={e => setDocType(e.target.value)} style={{ width: '100%', padding: '10px', borderRadius: '4px', border: '1px solid #ccc', color: '#333', backgroundColor: '#fff' }}>
          <option value="">Selecione um tipo primeiro...</option>
          <option value="sem_tipo">Sem Tipo</option>
          {documentTypes.map(dt => (
            <option key={dt._id} value={dt._id}>{dt.name}</option>
          ))}
        </select>
      </div>

      {docType ? (
        <>
          <TabContainer>
            <Tab $active={tab === 'search'} onClick={() => setTab('search')}>Vincular Existente</Tab>
            <Tab $active={tab === 'create'} onClick={() => setTab('create')}>Cadastrar Apenas Físico</Tab>
          </TabContainer>

          {tab === 'search' && (
            <div>
              <input 
                type="text" 
                placeholder="Digite o nome do documento (min 3 letras)..." 
                value={searchTerm} 
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{ width: '100%', padding: '10px', borderRadius: '4px', border: '1px solid #ccc', marginBottom: '15px', color: '#333', backgroundColor: '#fff' }}
              />
              
              <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
                {searching ? (
                  <p style={{ color: '#333' }}>Buscando...</p>
                ) : searchResults.length > 0 ? (
                  searchResults.filter(d => {
                    const hasNoType = !d.documentTypeWithData || !(d as any).documentTypeWithData.documentType;
                    if (docType === 'sem_tipo') {
                      return hasNoType;
                    }
                    if (hasNoType) return false;
                    
                    const typeId = typeof (d as any).documentTypeWithData.documentType === 'string' 
                                   ? (d as any).documentTypeWithData.documentType 
                                   : ((d as any).documentTypeWithData.documentType as any)._id || (d as any).documentTypeWithData.documentType;
                    return String(typeId) === String(docType);
                  }).length > 0 ? (
                    searchResults.filter(d => {
                      const hasNoType = !d.documentTypeWithData || !(d as any).documentTypeWithData.documentType;
                      if (docType === 'sem_tipo') {
                        return hasNoType;
                      }
                      if (hasNoType) return false;
                      
                      const typeId = typeof (d as any).documentTypeWithData.documentType === 'string' 
                                     ? (d as any).documentTypeWithData.documentType 
                                     : ((d as any).documentTypeWithData.documentType as any)._id || (d as any).documentTypeWithData.documentType;
                      return String(typeId) === String(docType);
                    }).map(doc => (
                      <SearchResult key={doc._id}>
                        <div style={{ flex: 1, overflow: 'hidden' }}>
                          <strong style={{ color: '#111' }}>{doc.name}</strong>
                          <div style={{ fontSize: '12px', color: '#666', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={buildPath(doc)}>
                            Pasta: {buildPath(doc)}
                          </div>
                        </div>
                        <div style={{ marginLeft: '10px' }}>
                          <Button label="Vincular" color="primary" variantType="small" onClick={() => handleLink(doc)} />
                        </div>
                      </SearchResult>
                    ))
                  ) : (
                    <p style={{ color: '#333' }}>Nenhum documento encontrado com este Tipo de Documento.</p>
                  )
                ) : searchTerm.length >= 3 ? (
                  <p style={{ color: '#333' }}>Nenhum documento encontrado.</p>
                ) : (
                  <p style={{ color: '#888' }}>Digite para buscar documentos já digitalizados no GED.</p>
                )}
              </div>
            </div>
          )}

          {tab === 'create' && (
            <FormContainer onSubmit={handleCreate}>
              <p style={{ color: '#555', marginBottom: '10px' }}>
                Use esta opção para registrar um documento que <strong>ainda não foi digitalizado</strong>, mas que está fisicamente guardado neste local.
              </p>
              <div>
                <label>Nome do Documento</label>
                <input 
                  type="text" 
                  value={docName} 
                  onChange={e => setDocName(e.target.value)} 
                  required 
                  placeholder="Ex: Contrato Fulano 2024"
                  style={{ color: '#333', backgroundColor: '#fff' }}
                />
              </div>
              <Button type="submit" label={creating ? 'Salvando...' : 'Salvar Documento Físico'} color="primary" variantType="block" onClick={handleCreate} disabled={creating || !docName.trim() || !docType} />
            </FormContainer>
          )}
        </>
      ) : (
        <p style={{ color: '#888', textAlign: 'center', marginTop: '30px' }}>Selecione o Tipo de Documento acima para continuar.</p>
      )}

    </Modal>
  );
};

export default ModalLinkDocument;
